import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmberOutlined';

export interface ConfirmationReference {
  label: string;
  value: string;
}

export interface BookingConfirmationProps {
  status: 'success' | 'partial';
  title?: ReactNode;
  message?: ReactNode;
  references?: ConfirmationReference[];
  /** İtinerary / sepet satırları. */
  summary?: ReactNode;
  /** Belge menüsü / voucher butonları. */
  documents?: ReactNode;
  primaryAction?: { label: string; to?: string; onClick?: () => void };
  secondaryActions?: ReactNode;
}

/** Akış sonu onay ekranı — otel voucher, otobüs/transfer/araç özet kartları ve uçak
 *  "biletlendi" durumunun ortak yeri. */
export function BookingConfirmation({
  status,
  title,
  message,
  references,
  summary,
  documents,
  primaryAction,
  secondaryActions,
}: BookingConfirmationProps) {
  const ok = status === 'success';
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 1 }}>
          {ok ? (
            <CheckCircleOutlineIcon color="success" />
          ) : (
            <WarningAmberIcon color="warning" />
          )}
          <Typography variant="h6">{title ?? (ok ? 'Rezervasyon tamamlandı' : 'Rezervasyon kısmi tamamlandı')}</Typography>
        </Stack>

        {message && (
          <Alert severity={ok ? 'success' : 'warning'} sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        {references && references.length > 0 && (
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', mb: 2 }}>
            {references.map((ref) => (
              <Box key={ref.label}>
                <Typography variant="caption" color="text.secondary" component="div">
                  {ref.label}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                  {ref.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {summary && (
          <>
            <Divider sx={{ my: 2 }} />
            {summary}
          </>
        )}

        {(documents || primaryAction || secondaryActions) && (
          <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            {documents}
            {secondaryActions}
            {primaryAction &&
              (primaryAction.to ? (
                <Button variant="contained" component={RouterLink} to={primaryAction.to}>
                  {primaryAction.label}
                </Button>
              ) : (
                <Button variant="contained" onClick={primaryAction.onClick}>
                  {primaryAction.label}
                </Button>
              ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
